import React, { useState } from "react";
import { twMerge } from "tailwind-merge";
import { type PillarApiError } from "shared/api/PillarApiError";
import AlertTriangleIcon from "shared/components/icons/AlertTriangleIcon";
import ChevronDownIcon from "shared/components/icons/chevron/ChevronDownIcon";
import ChevronUpIcon from "shared/components/icons/chevron/ChevronUpIcon";

type PillarTableErrorMessageProps = {
  /** The failure to show. Its message is already what `handleError` let this caller see. */
  error: PillarApiError;
  /** Test id prefix; the message and the Details toggle get `-message` and `-details`. */
  testid: string;
  /** Actions shown under the message, such as Try Again or Dismiss. */
  children?: React.ReactNode;
  /**
   * One line, message then actions, small and wrapping, so it fits in a
   * row's action cell without widening the column.
   */
  compact?: boolean;
};

/**
 * How every PillarTable failure reads: the message, the server stack behind
 * Details when the caller was allowed one, then whatever the caller can do
 * about it.
 */
const PillarTableErrorMessage = ({
  error,
  testid,
  children,
  compact = false,
}: PillarTableErrorMessageProps) => {
  const [stackExpanded, setStackExpanded] = useState(false);

  const detailsToggle = error.serverStack && (
    <button
      type="button"
      className={twMerge(
        "flex justify-center items-center gap-x-0.5 cursor-pointer text-sm",
        compact ? "shrink-0 whitespace-nowrap" : "mx-auto",
      )}
      aria-expanded={stackExpanded}
      data-testid={`${testid}-details`}
      onClick={() => setStackExpanded((expanded) => !expanded)}
    >
      {stackExpanded ? (
        <ChevronUpIcon className="h-2 w-2" />
      ) : (
        <ChevronDownIcon className="h-2 w-2" />
      )}
      Details
    </button>
  );

  const stack = error.serverStack && (
    <pre
      className={`max-w-full max-h-4xl overflow-auto whitespace-pre-wrap break-words text-left text-sm ${
        stackExpanded ? "" : "hidden"
      }`}
    >
      {error.serverStack}
    </pre>
  );

  if (compact) {
    return (
      <div className="flex flex-col gap-y-0.5 text-sm text-left">
        <div className="flex items-center gap-x-1">
          <div className="flex flex-1 min-w-0 items-start gap-x-0.5 text-status-danger-tint">
            <AlertTriangleIcon className="shrink-0 h-2 w-2 mt-0.25" />
            <span data-testid={`${testid}-message`}>{error.message}</span>
          </div>
          {detailsToggle}
          <div className="shrink-0">{children}</div>
        </div>
        {stack}
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center gap-y-0.5 py-1">
      <div className="flex justify-center items-center gap-x-0.5 text-status-danger-tint">
        <AlertTriangleIcon className="shrink-0" />
        <span data-testid={`${testid}-message`}>{error.message}</span>
      </div>
      {error.serverStack && (
        <div className="max-w-full">
          {detailsToggle}
          {stack}
        </div>
      )}
      {children}
    </div>
  );
};

export default PillarTableErrorMessage;
